import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { useToastContext } from '@/contexts/toastContext';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import financeClientsService from '@/services/financeClients';
import financeInvoicesService, { FxInvoiceLine } from '@/services/financeInvoices';
import { FxInvoice } from '@/types/api/finance';
import FinanceXInvoiceDetailComponent from './FinanceXInvoiceDetail.component';

type AvailableProfile = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  developerType: string | null;
};

const FinanceXInvoiceDetailContainer = (): JSX.Element => {
  const { invoiceId = '' } = useParams();
  const queryClient = useQueryClient();
  const toast = useToastContext();

  const { data: invoice, isLoading: isLoadingInvoice } = useSeoFetch<FxInvoice>(
    ['fx-invoice-detail', invoiceId],
    () => financeInvoicesService.getById(invoiceId),
    { enabled: !!invoiceId, retry: false }
  );

  const { data: lines = [], isLoading: isLoadingLines } = useSeoFetch<FxInvoiceLine[]>(
    ['fx-invoice-lines', invoice?.id],
    () => financeInvoicesService.getLines(invoice!.id),
    { enabled: !!invoice?.id, retry: false }
  );

  const { data: profiles = [] } = useSeoFetch<AvailableProfile[]>(
    ['fx-available-profiles'],
    () => financeClientsService.getAvailableProfiles(),
    { retry: false }
  );

  const handleLinkProfile = useCallback(
    async (lineId: string, profileId: string | null) => {
      try {
        const profile = profileId ? profiles.find((p) => p.id === profileId) : null;
        const profileName = profile ? `${profile.firstName} ${profile.lastName}`.trim() : null;
        await financeInvoicesService.updateLineProfile(lineId, profileId, profileName);
        queryClient.invalidateQueries(['fx-invoice-lines', invoiceId]);
      } catch {
        toast.showErrorFeedback('Failed to link profile.');
      }
    },
    [profiles, invoiceId, queryClient, toast]
  );

  return (
    <FinanceXInvoiceDetailComponent
      invoice={invoice ?? null}
      lines={lines}
      isLoading={isLoadingInvoice || isLoadingLines}
      profiles={profiles}
      onLinkProfile={handleLinkProfile}
    />
  );
};

export default FinanceXInvoiceDetailContainer;
